import { getAngularService } from '@kathondvla/react-shared-components/src/helpers/angularReactHelper';
import { stripHtml } from '@newStore/genericHelpers';
import juice from 'juice';
import wordFilter from 'tinymce-word-paste-filter';
import tinymce, { type Editor } from 'tinymce/tinymce';

export const setupButton = (
  editor: Editor,
  name: string,
  tooltip: string,
  svgIcon: string,
  // eslint-disable-next-line no-shadow
  onAction: (editor: Editor) => void,
  // eslint-disable-next-line no-shadow
  isEnabledButton: (editor: Editor) => boolean,
) => {
  const iconName = `${name}Icon`;
  editor.ui.registry.addIcon(iconName, svgIcon);

  editor.ui.registry.addButton(name, {
    icon: iconName,
    tooltip,
    onAction: () => {
      onAction(editor);
    },
    onSetup: (buttonApi) => {
      const editorEventCallback = () => {
        buttonApi.setEnabled(isEnabledButton(editor));
      };
      editor.on('NodeChange SelectionChange', editorEventCallback);
      return () => {
        editor.off('NodeChange SelectionChange', editorEventCallback);
      };
    },
  });

  editor.ui.registry.addMenuItem(name, {
    icon: iconName,
    onAction: () => {
      onAction(editor);
    },
  });
};

export const registerButton = (
  name: string,
  tooltip: string,
  svgIcon: string,
  onAction: (editor: Editor) => void,
  isEnabledButton: (editor: Editor) => boolean,
) => {
  const exists = tinymce.PluginManager.get(name);
  if (!exists) {
    tinymce.PluginManager.add(name, (editor) =>
      setupButton(editor, name, tooltip, svgIcon, onAction, isEnabledButton),
    );
  }
};

export const openModal = async (
  editor: Editor,
  modalOptions: {
    component: string;
    dataForModal?: object;
  },
  renderFunction: (modalResult: object, selectedText: string) => string,
) => {
  editor.setDirty(false);
  if (!editor.selection.getContent()) {
    // If the user clicked on a button without selected text, we get the node which contains the caret
    editor.selection.select(editor.selection.getNode());
  }
  const bookmark = editor.selection.getBookmark(2);
  const modalWindowService = getAngularService('ModalWindowService');

  const modalResult = await modalWindowService.open(modalOptions);
  editor.selection.moveToBookmark(bookmark);
  editor.selection.setContent(
    renderFunction(modalResult, stripHtml(editor.selection.getContent())),
  );
  editor.setDirty(true);
  editor.focus();
};

/**
 * Word stamps `display="block"` on pasted MathML, which forces a line break before
 * and after the equation and blocks inline use without editing the raw MathML. Drop
 * it from the root `<math>` tag so the equation keeps MathML's default inline flow.
 */
const stripMathDisplayBlock = (mathml: string): string =>
  mathml.replace(/(<math\b[^>]*?)\s+display\s*=\s*(["'])block\2/i, '$1');

/**
 * Wraps MathML in a non-editable span so TinyMCE treats it as an atomic inline
 * block: the cursor cannot wander inside, and a single Backspace/Delete removes
 * the whole element. The class is what survives serialisation; TinyMCE
 * re-applies contenteditable="false" on every content load via noneditable_class.
 */
export const wrapMathML = (mathml: string): string =>
  `<span contenteditable="false" class="math-node">${stripMathDisplayBlock(mathml)}</span>`;

const MATHML_NS = 'http://www.w3.org/1998/Math/MathML';

const escapeXml = (text: string): string =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Converts a single OMML run (`m:r`) to the fitting MathML token element. */
const convertOmmlRun = (node: Element): string => {
  const text = (node.textContent ?? '').trim();
  if (!text) return '';
  if (/^\d+([.,]\d+)?$/.test(text)) return `<mn>${escapeXml(text)}</mn>`;
  if (/^[A-Za-z]+$/.test(text)) return `<mi>${escapeXml(text)}</mi>`;
  return `<mo>${escapeXml(text)}</mo>`;
};

const convertOmmlChildren = (node: Element | null): string => {
  if (!node) return '';
  let result = '';
  node.childNodes.forEach((child) => {
    result += convertOmmlNode(child);
  });
  return result;
};

/** Recursively converts an OMML node (m: prefix stripped) to a MathML string. */
const convertOmmlNode = (node: ChildNode): string => {
  if (node.nodeType !== Node.ELEMENT_NODE) return '';
  const el = node as Element;
  const tag = el.tagName.toLowerCase();
  // Skip property containers (fPr, ctrlPr, rPr, dPr, radPr, ...): formatting, not content.
  if (tag.endsWith('pr')) return '';
  switch (tag) {
    case 'f':
      return `<mfrac><mrow>${convertOmmlChildren(el.querySelector(':scope > num'))}</mrow><mrow>${convertOmmlChildren(el.querySelector(':scope > den'))}</mrow></mfrac>`;
    case 'ssup':
      return `<msup><mrow>${convertOmmlChildren(el.querySelector(':scope > e'))}</mrow><mrow>${convertOmmlChildren(el.querySelector(':scope > sup'))}</mrow></msup>`;
    case 'ssub':
      return `<msub><mrow>${convertOmmlChildren(el.querySelector(':scope > e'))}</mrow><mrow>${convertOmmlChildren(el.querySelector(':scope > sub'))}</mrow></msub>`;
    case 'ssubsup':
      return `<msubsup><mrow>${convertOmmlChildren(el.querySelector(':scope > e'))}</mrow><mrow>${convertOmmlChildren(el.querySelector(':scope > sub'))}</mrow><mrow>${convertOmmlChildren(el.querySelector(':scope > sup'))}</mrow></msubsup>`;
    case 'rad': {
      const degree = convertOmmlChildren(el.querySelector(':scope > deg'));
      const radicand = convertOmmlChildren(el.querySelector(':scope > e'));
      return degree
        ? `<mroot><mrow>${radicand}</mrow><mrow>${degree}</mrow></mroot>`
        : `<msqrt>${radicand}</msqrt>`;
    }
    case 'd': {
      const parts = Array.from(el.querySelectorAll(':scope > e')).map((e) =>
        convertOmmlChildren(e),
      );
      return `<mrow><mo>(</mo>${parts.join('<mo>,</mo>')}<mo>)</mo></mrow>`;
    }
    case 'r':
      return convertOmmlRun(el);
    // Containers/wrappers: emit their children as-is.
    default:
      return convertOmmlChildren(el);
  }
};

/**
 * Converts a Word OMML equation (`m:oMath`) to a MathML string. The fragment comes
 * from an IE conditional comment and is interleaved with Word's HTML styling tags
 * (`span`, `i`, ...), which are stripped before parsing.
 */
const convertOmmlToMathML = (ommlFragment: string): string => {
  const match = ommlFragment.match(/<m:oMath[\s>][\s\S]*?<\/m:oMath>/i);
  if (!match) return '';
  // Drop the interleaved HTML styling tags, keep only OMML (m:*) elements and their text.
  const withoutHtml = match[0].replace(/<\/?(?!m:)[a-zA-Z][^>]*>/g, '');
  // Strip the m: prefix so the fragment parses as plain HTML and can be walked by tag name.
  const stripped = withoutHtml.replace(/<(\/?)m:/g, '<$1');
  const root = new DOMParser().parseFromString(stripped, 'text/html').body.querySelector('omath');
  const body = convertOmmlChildren(root);
  return body ? `<math xmlns="${MATHML_NS}"><mrow>${body}</mrow></math>` : '';
};

/**
 * Removes the down-level bitmap fallback Word emits next to each equation
 * (`<![if !msEquation]> ...<img>... <![endif]>`, possibly with nested conditionals),
 * so the equation is not duplicated once its OMML has been turned into MathML.
 */
const stripWordEquationFallback = (html: string): string => {
  const tokenRe = /<!\[if\b([^\]]*)\]>|<!\[endif\]>/gi;
  let out = '';
  let last = 0;
  let depth = 0;
  let token = tokenRe.exec(html);
  while (token !== null) {
    const isOpen = token[1] !== undefined;
    if (depth === 0) {
      if (isOpen && /!\s*msEquation/i.test(token[1])) {
        out += html.slice(last, token.index);
        depth = 1;
        last = tokenRe.lastIndex;
      }
    } else if (isOpen) {
      depth += 1;
    } else {
      depth -= 1;
      if (depth === 0) last = tokenRe.lastIndex;
    }
    token = tokenRe.exec(html);
  }
  out += html.slice(last);
  // Drop any leftover down-level conditional markers.
  return out.replace(/<!\[if\b[^\]]*\]>/gi, '').replace(/<!\[endif\]>/gi, '');
};

export const pasteHtml = async (evt: ClipboardEvent, editor: Editor | null) => {
  evt.preventDefault();
  evt.stopPropagation();
  let pastedData = evt.clipboardData?.getData('text/html');
  if (!pastedData) {
    // Fallback for content which is not copied as HTML
    pastedData = await navigator.clipboard.readText();
  }
  if (!editor || !pastedData) return;

  // A pure equation selection: Word puts real MathML in text/plain, which is more
  // accurate than converting the OMML ourselves. Only take this path when the plain
  // text is nothing but a single MathML block (so mixed content still goes through
  // the OMML conversion below).
  const plainText = evt.clipboardData?.getData('text/plain') ?? '';
  if (/^\s*<math[\s>][\s\S]*<\/math>\s*$/i.test(plainText)) {
    editor.selection.setContent(wrapMathML(plainText.trim()));
    editor.setDirty(true);
    return;
  }

  const mathBlocks: string[] = [];
  const addPlaceholder = (wrappedMathML: string): string =>
    `__MATH_PLACEHOLDER_${mathBlocks.push(wrappedMathML) - 1}__`;

  let html = pastedData;

  // Word equations are OMML, hidden in `<!--[if gte msEquation ...]>` comments, with a
  // bitmap fallback rendered next to them. Convert each equation to MathML, replace it
  // with a placeholder, and drop the bitmap fallback so it is not duplicated.
  if (/<m:oMath[\s>]/i.test(html)) {
    html = html.replace(
      /<!--\[if[^\]]*msEquation[^\]]*\]>([\s\S]*?)<!\[endif\]-->/gi,
      (_full, commentBody) => {
        const mathml = convertOmmlToMathML(commentBody);
        return mathml ? addPlaceholder(wrapMathML(mathml)) : '';
      },
    );
    html = stripWordEquationFallback(html);
  }

  // Extract any real MathML blocks and replace with placeholders so the normal cleanup
  // pipeline (MSO style stripping, wordFilter, juice) runs on the surrounding HTML
  // without corrupting the MathML markup.
  html = html.replace(/<math[\s>][\s\S]*?<\/math>/gi, (match) => addPlaceholder(wrapMathML(match)));

  // Remove MS styles that can break the parsing
  const cleaned = html.replace(/(mso-[\w-]*:[^'";]*;?)/gim, '');
  let filteredData = juice(
    `<style>table{min-width: 50%; max-width: 100%; display: table}</style>${wordFilter(cleaned)}`,
  );

  // Restore MathML blocks
  filteredData = filteredData.replace(
    /__MATH_PLACEHOLDER_(\d+)__/g,
    (_, i) => mathBlocks[Number(i)],
  );

  editor.selection.setContent(filteredData);
  editor.setDirty(true);
};
